import {
    fromNullable,
    fromPredicate,
    none,
    Option,
    some,
} from 'fp-ts/lib/Option';
import {
    OsmObject,
    OsmElementNode,
    OsmType,
    OsmElementWay,
    fetchOsmNode,
    OsmElementRelation,
    fetchOsmWay,
    OsmElement,
    Tag,
    TagForm,
    TagState,
} from '../remote';
import { index } from 'fp-ts/lib/Array';
import { dispatchK, dispatch, assign } from 'sdi/shape';
import { Collection, mapCollectionC, updateCollection } from 'sdi/util';
import { getForm, getOsmRemote } from '../queries/osm';
import { Pair, fst, pair, snd } from 'sdi/lib';
import { updateTags } from './qa';

const dispatchNode = dispatchK('qa/osm/nodes');
const dispatchWay = dispatchK('qa/osm/ways');
const dispatchRelation = dispatchK('qa/osm/relations');

const { registerPromise, updatePromise } = (() => {
    type RR = Pair<() => void, (s: OsmDownloadState) => void>;
    const col: { [key: string]: RR } = {};

    const registerPromise = (
        key: string,
        resolve: (s: OsmDownloadState) => void,
        reject: () => void
    ) => {
        if (key in col) {
            fst(col[key])();
        }
        col[key] = pair(reject, resolve);
    };

    const updatePromise = (key: string, ods: OsmDownloadState) => {
        if (key in col) {
            const reject = fst(col[key]);
            const resolve = snd(col[key]);
            if (ods.cancel) {
                reject();
            } else if (ods.total === ods.done) {
                resolve(ods);
            }
        }
    };

    return { registerPromise, updatePromise };
})();

const dispatchRemote = (key: string, ods: OsmDownloadState) => {
    dispatch('qa/osm/remote-state', state => updateCollection(state, key, ods));
    updatePromise(key, ods);
};

export interface OsmDownloadState {
    done: number;
    total: number;
    cancel: boolean;
}

const newDownloadState = (total: number): OsmDownloadState => ({
    total,
    done: 0,
    cancel: false,
});

const mapState =
    (f: (ods: OsmDownloadState) => void) =>
    (ods: OsmDownloadState | Readonly<OsmDownloadState>) => {
        const newOds: OsmDownloadState = { ...ods };
        f(newOds);
        return newOds;
    };

// const completeState = mapState((ods) => { ods.done = ods.total; });
const incrDone = mapState(ods => {
    ods.done += 1;
});
const incrTotal = mapState(ods => {
    ods.total += 1;
});
const cancel = mapState(ods => {
    ods.cancel = true;
});

export const notCanceled = fromPredicate<OsmDownloadState>(ods => !ods.cancel);
export const notDone = fromPredicate<OsmDownloadState>(
    ods => ods.total > ods.done
);
export const done = fromPredicate<OsmDownloadState>(
    ods => !ods.cancel && ods.total === ods.done
);

const key = (id: number) => id.toString();
export const remoteKey = (t: OsmType, id: number, version: number) =>
    `${t}-${id}-${version}`;

const updateCollectionL = <T>(
    coll: Collection<T>,
    key: string,
    value: (old: Option<T>) => T
) => {
    coll[key] = value(fromNullable(coll[key]));
    return coll;
};

const loadNode = (el: OsmElementNode, rk: string, ods: OsmDownloadState) => {
    const newOds = incrDone(ods);
    dispatchNode(nodes =>
        updateCollectionL(nodes, key(el.id), opt =>
            opt
                .map(versions =>
                    versions
                        .filter(v => v.version !== el.version)
                        .concat(el)
                        .sort((a, b) => a.version - b.version)
                )
                .getOrElse([el])
        )
    );
    dispatchRemote(rk, newOds);
    return newOds;
};

const cursor = <T>(xs: T[]) => {
    let idx = -1;
    return () => {
        const cur = idx + 1;
        if (cur >= xs.length) {
            return none;
        }
        idx = cur;
        return some(xs[idx]);
    };
};

const loadWay = (el: OsmElementWay, rk: string, ods: OsmDownloadState) => {
    dispatchWay(ways =>
        updateCollectionL(ways, key(el.id), opt =>
            opt
                .map(versions =>
                    versions
                        .filter(v => v.version !== el.version)
                        .concat(el)
                        .sort((a, b) => a.version - b.version)
                )
                .getOrElse([el])
        )
    );

    dispatchRemote(
        rk,
        mapState(o => {
            o.total += el.nodes?.length ?? 0;
        })(ods)
    );

    if (el.nodes !== undefined && el.nodes.length > 0) {
        const getId = cursor(el.nodes);
        const next = () =>
            getId().map(id =>
                fetchOsmNode(id)
                    .then(element =>
                        getOsmRemote(rk).map(ods => {
                            notDone(loadNode(element, rk, ods)).map(_ods =>
                                next()
                            );
                        })
                    )
                    .catch(_err => dispatchRemote(rk, cancel(ods)))
            );
        next();
    }
};

const loadRemoteWay = (rk: string, id: number) =>
    fetchOsmWay(id)
        .then(way =>
            getOsmRemote(rk)
                .chain(notCanceled)
                .map(ods => {
                    dispatchRemote(rk, incrDone(ods));
                    loadWay(way, rk, ods);
                })
        )
        .catch(_err =>
            getOsmRemote(rk).map(ods => dispatchRemote(rk, cancel(ods)))
        );

const loadRemoteNode = (rk: string, id: number) =>
    fetchOsmNode(id)
        .then(node =>
            getOsmRemote(rk)
                .chain(notCanceled)
                .map(ods => {
                    loadNode(node, rk, ods);
                })
        )
        .catch(_err =>
            getOsmRemote(rk).map(ods => dispatchRemote(rk, cancel(ods)))
        );

const loadRelation = (
    el: OsmElementRelation,
    rk: string,
    ods: OsmDownloadState
) => {
    dispatchRelation(relations =>
        updateCollectionL(relations, key(el.id), opt =>
            opt
                .map(versions =>
                    versions
                        .filter(v => v.version !== el.version)
                        .concat(el)
                        .sort((a, b) => a.version - b.version)
                )
                .getOrElse([el])
        )
    );

    // dispatchRemote(rk, mapState((o) => { o.total += el.members.length; })(ods));

    const getMember = cursor(el.members);

    const next = () =>
        getMember().map(member => {
            dispatchRemote(rk, incrTotal(ods));
            switch (member.type) {
                case 'node': {
                    loadRemoteNode(rk, member.ref).then(next);
                    break;
                }
                case 'way': {
                    loadRemoteWay(rk, member.ref).then(next);
                    break;
                }
                /**
                 * from https://wiki.openstreetmap.org/wiki/Super-Relation
                 * "Nested relations are virtually undocumented and rarely used"
                 */
                case 'relation': {
                }
            }
        });

    next();
};

export const loadFullObject = (obj: OsmObject) =>
    new Promise<OsmDownloadState>((resolve, reject) =>
        index(0, obj.elements).foldL(reject, el => {
            const key = remoteKey(el.type, el.id, el.version);
            registerPromise(key, resolve, reject);
            switch (el.type) {
                case 'node':
                    return loadNode(el, key, newDownloadState(1));
                case 'way':
                    return loadWay(el, key, newDownloadState(0));
                case 'relation':
                    return loadRelation(el, key, newDownloadState(0));
            }
        })
    );

export const initTagForm = (element: OsmElement, issueId: number) => {
    const tags = fromNullable(element.tags)
        .map(
            mapCollectionC(
                (k, v) =>
                    ({
                        pair: pair(k, v),
                        state: 'none',
                    } as Tag)
            )
        )
        .getOrElse([]);
    const tagForm = {
        issueId,
        tags,
        comment: '#transilien', //TODO: other default?
        source: '',
    };
    assign('qa/osm/tags', tagForm);
};

export const clearTagForm = () => assign('qa/osm/tags', null);

export const setFormContent = (tagForm: TagForm) =>
    assign('qa/osm/tags', tagForm);

const getNewForm = (newTags: Tag[], oldForm: TagForm): TagForm => {
    return {
        issueId: oldForm.issueId,
        tags: newTags,
        comment: oldForm.comment,
        source: oldForm.source,
    };
};

export const changeFormValue = (newValue: string, idx: number) => {
    getForm()
        .map(f => {
            const newTags = f.tags.map<Tag>((tag, index) => {
                if (index === idx) {
                    const oldValue = snd(tag.pair);
                    const newState =
                        tag.state === 'created'
                            ? 'created'
                            : oldValue !== newValue
                            ? 'modified'
                            : tag.state;
                    return {
                        pair: pair(fst(tag.pair), newValue),
                        state: newState,
                    };
                } else {
                    return tag;
                }
            });
            return getNewForm(newTags, f);
        })
        .map(setFormContent);
};

export const changeFormKey = (newKey: string, idx: number) => {
    getForm()
        .map(f => {
            const newTags = f.tags.map<Tag>((tag, index) => {
                if (index === idx) {
                    const oldKey = fst(tag.pair);
                    const newState =
                        tag.state === 'created'
                            ? 'created'
                            : oldKey !== newKey
                            ? 'modified'
                            : tag.state;
                    return {
                        pair: pair(newKey, snd(tag.pair)),
                        state: newState,
                    };
                } else {
                    return tag;
                }
            });
            return getNewForm(newTags, f);
        })
        .map(setFormContent);
};

export const changeFormComment = (comment: string) =>
    getForm()
        .map(f => ({
            issueId: f.issueId,
            tags: f.tags,
            comment,
            source: f.source,
        }))
        .map(tagForm => assign('qa/osm/tags', tagForm));

export const changeFormSource = (source: string) =>
    getForm()
        .map(f => ({
            issueId: f.issueId,
            tags: f.tags,
            comment: f.comment,
            source,
        }))
        .map(tagForm => assign('qa/osm/tags', tagForm));

export const addFormKey = (newKey: string) =>
    getForm()
        .map(f => {
            const newTags = f.tags;
            newTags.push({
                pair: pair(newKey, ''),
                state: 'created',
            });
            return getNewForm(newTags, f);
        })
        .map(setFormContent);

export const submitFormTag = () =>
    getForm().map(f => {
        const tags = f.tags.filter(t => t.state != 'deleted');
        const newForm = getNewForm(tags, f);
        updateTags(newForm);
    });

export const addFormTag = () => {
    const emptyTag: Tag = { pair: pair('', ''), state: 'created' };
    getForm()
        .map(f => {
            const newTags = f.tags;
            newTags.push(emptyTag);
            return getNewForm(newTags, f);
        })
        .map(setFormContent);
};

export const changeTagState = (newState: TagState, idx: number) => {
    getForm()
        .map(f => {
            const newTags = f.tags.map<Tag>((tag, index) => {
                if (index === idx) {
                    return {
                        pair: tag.pair,
                        state: newState,
                    };
                } else {
                    return tag;
                }
            });
            return getNewForm(newTags, f);
        })
        .map(setFormContent);
};

export const deleteFormTag = (idx: number) => {
    getForm()
        .map(f => {
            const newTags = f.tags.filter((_, index) => idx != index);
            return getNewForm(newTags, f);
        })
        .map(setFormContent);
};
